import * as React from "react";
import {VeBoxCard} from "../../components/card/VeBoxCard";
import {InputAddon, List, TextInput} from "@vebox/etui-core";
import {Loader} from "../../components/Utils/loader/Loader";
import {useUserActivities} from "../contexts/UserActivitiesContext";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {useCommonData} from "../contexts/CommonDataProvider";
import {VeboxModels} from "../../types/veboxModels";
import {Display} from "../../components/Display";
import {SearchIcon} from "../components/SearchIcon";

declare var AppTranslations: AppTranslations;

type UserActivitiesProps = {}

const ACTIVITY_TEXT = []

export type Activity = {
    id: number,
    user: string,
    activityDate: string,
    activityText: string,
    activityNotes: string,
    actionGuid: string,
    param: ActivityParams
}

export type ActivityParams = {
    field: string
    translationKey: string
    oldValue: string | string []
    newValue: string | string []
}

export const UserActivities: React.FC<UserActivitiesProps> = () => {

    const [activities, isLoading] = useUserActivities();
    const [searchWord, setSearchWord] = React.useState("");
    const [showSearchActivityBar, setShowSearchActivityBar] = React.useState<boolean>(false);

    const searchActivityBarRef = React.useRef(null)
    const clearSearchWord = () => setSearchWord("");
    const changeSearchWord = (e: React.ChangeEvent<HTMLInputElement>) => setSearchWord(e.target.value);

    const showHideSearchActivityBar = () => {
        setShowSearchActivityBar(prevState => {
            if (prevState) {
                clearSearchWord()
            }
            if (searchActivityBarRef && !prevState) {
                searchActivityBarRef.current.focus();
            }
            return !prevState
        });
    }

    React.useEffect(() => {
        if (searchActivityBarRef && showSearchActivityBar) {
            searchActivityBarRef.current.focus();
        }
    }, [showSearchActivityBar])

    return <VeBoxCard className={"Section"}>
        <VeBoxCard.Header
            title={AppTranslations.usersManagement.usersActivities}
            singleLineTitle={true}
            back={
                <Display isVisible={!isLoading && activities.length > 0}>
                    <SearchIcon
                        showHideSearchBar={showHideSearchActivityBar}
                        openSearchBarMessage={AppTranslations.filters.filterBy}
                    />
                </Display>
            }
        />
        <VeBoxCard.Body style={{height: "470px"}} className={"detail-card"}>
            <Display isVisible={isLoading}>
                <Loader/>
            </Display>
            <Display isVisible={!isLoading}>
                <Display isVisible={activities.length > 0}>
                    <TextInput
                        elRef={searchActivityBarRef}
                        label={AppTranslations.filters.filterBy}
                        styles={{
                            input: {boxSizing: "border-box"},
                            root: {display: (!showSearchActivityBar ? "none" : "")}
                        }}
                        className={"margin-bottom-10"}
                        value={searchWord}
                        onChange={changeSearchWord}
                        endInputAddon={
                            <InputAddon position={"end"}>
                                {Boolean(searchWord) && <span
                                    className="material-icons-outlined clickable"
                                    onClick={clearSearchWord}
                                >clear</span>}
                            </InputAddon>
                        }
                    />
                </Display>
                <div className={"users-management-list-container"}>
                    {activities.length > 0 && (<List
                        dataSource={activities.filter(
                            (act) => act.activityText.toLowerCase().includes(searchWord.toLowerCase())
                                || act.activityDate.toLowerCase().includes(searchWord.toLowerCase())
                                || act.user.toLowerCase().includes(searchWord.toLowerCase()))}
                        mode="compact"
                        renderItem={renderItem}
                    />)}

                    {activities.length === 0 && (
                        <div className="userActivityListElement">
                            <p>{AppTranslations.usersManagement.noActivities}</p>
                        </div>
                    )}
                </div>
            </Display>
        </VeBoxCard.Body>
    </VeBoxCard>
}


type paramProps = {
    value: string
}
const Param: React.FC<paramProps> = (props) => {
    return (
        <li>
            {props.value}
        </li>
    )
}

type paramListProps = {
    list: string[]
    isRolesList: boolean
}
const ParamList: React.FC<paramListProps> = (props) => {
    const {roles: rolesManaged} = useCommonData();
    return (
        <ul className={"param-list"}>
            {props.list.map((val, index) => {
                const role: VeboxModels.Role | undefined = rolesManaged.find((r) => r.code === val)
                return <Param
                    key={index}
                    value={props.isRolesList
                        ? role?.name ?? AppTranslations.usersManagement.userLogNotAvailable
                        : val
                    }
                />
            })}
        </ul>
    )
}

const splitActivityText = (): string[] => {
    const activityText: string[] = AppTranslations.usersManagement.hasChangedFieldFromTo.split("{" + 0 + "}")

    for (let i = 1; i < 3; i++) {
        const tmp = activityText[i].split("{" + i + "}")
        activityText[i] = tmp[0].slice(0, tmp[0].length - 1);
        activityText.push(tmp[1])
    }
    activityText.pop()
    return activityText;
}
const prepareText = (field: string): string => {
    if (field === 'company') {
        return ACTIVITY_TEXT[0].concat(AppTranslations.filters.usersFilters.company.toLowerCase());
    } else {
        return ACTIVITY_TEXT[0].concat(AppTranslations.filters.usersFilters.roles.toLowerCase());
    }
}

const renderItem = (item: Activity) => {

    const customText: boolean = (item.param.field === 'company' || item.param.field === 'roles');
    if (ACTIVITY_TEXT.length === 0) {
        ACTIVITY_TEXT.push(...splitActivityText())
    }

    let activityText: string;
    if (customText) {
        activityText = prepareText(item.param.field);
    }

    return <List.Item>
        <div className="userActivityListElement">
            <div className="flx-row">
                <p className="activityDate">{item.activityDate}</p>
                <p>
                    <span className="activityUser">{item.user + " "}</span>
                    {!customText
                        ? <span className="activityText">{item.activityText}</span>
                        : <>
                            <span className="activityText">{activityText + ACTIVITY_TEXT[1].concat(":")}</span>
                            <ParamList
                                list={item.param.oldValue as string[]}
                                isRolesList={item.param.field === 'roles'}
                            />
                            <span className="activityText">{ACTIVITY_TEXT[2].concat(":")}</span>
                            <ParamList
                                list={item.param.newValue as string[]}
                                isRolesList={item.param.field === 'roles'}
                            />
                        </>
                    }
                </p>
            </div>
        </div>
    </List.Item>
}